import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/EngagementPostMissingMetricsData.graphql';
import { Query, QueryengagementPostMissingMetricsDataArgs } from '../../__generated__/globalTypes';

export const useEngagementPostMissingMetricsDataQuery = (
  options?: QueryHookOptions<Query, QueryengagementPostMissingMetricsDataArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryengagementPostMissingMetricsDataArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useEngagementPostMissingMetricsDataLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryengagementPostMissingMetricsDataArgs>
): QueryResult<Query, QueryengagementPostMissingMetricsDataArgs> & {
  getEngagementPostMissingMetricsData: LazyQueryExecFunction<Query, QueryengagementPostMissingMetricsDataArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getEngagementPostMissingMetricsData, result] = useLazyQuery<Query, QueryengagementPostMissingMetricsDataArgs>(
    QUERY,
    {
      onError: (error) => {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      },
      ...options
    }
  );

  return { getEngagementPostMissingMetricsData, ...result };
};

export const useEngagementPostMissingMetricsDataPromiseQuery = () => {
  const client = useApolloClient();

  const getEngagementPostMissingMetricsData = useCallback(
    (options?: Omit<QueryOptions<QueryengagementPostMissingMetricsDataArgs, Query>, 'query'>) =>
      client.query<Query, QueryengagementPostMissingMetricsDataArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getEngagementPostMissingMetricsData };
};
// Suspense Query Hooks
export const useEngagementPostMissingMetricsDataSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryengagementPostMissingMetricsDataArgs>
) => {
  const result = useSuspenseQuery<Query, QueryengagementPostMissingMetricsDataArgs>(QUERY, {
    ...options
  });

  return result;
};
